import {Add, ArrowBack, DeleteRounded, DescriptionRounded, ExpandMore} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary, Box,
    Button, FormControlLabel,
    Grid, IconButton, Stack, Switch,
    TextField, Tooltip, Typography
} from "@mui/material";
import {useContext, useEffect, useRef, useState} from "react";
import {Controller, useFormContext, useFormState} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import * as React from "react";
import {ColDef, GetRowIdParams, GridOptions, ICellRendererParams} from "ag-grid-enterprise";
import dayjs from "dayjs";
import {setSnackbar} from "../../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../../util/state/globalVariables";
import {useAgGridLocale, useHasScope} from "../../../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {useTheftMisappropriationActions} from "../../../../service/useTheftMisappropriationActions";
import {GlobalContext} from "../../../../../../util/state/GlobalContextProvider";
import {useRecoilValue} from "recoil";
import {EDITING} from "../../../../service/FinesSecurityRecoil";
import {DatePicker} from "@mui/x-date-pickers";
import CustomModalBL from "../../../../component/CustomModalBL";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
    THEFT_MISAPPROPRIATION_SCOPE_EDIT,
    THEFT_MISAPPROPRIATION_SCOPE_EDIT_TO_BE_CHARGED
} from "../../../../component/utils/AuthConstants";


const InvoicingDetails = (props: any) => {
    const {t} = useTranslation(['finessecurity'], {keyPrefix: 'theftMisappropriate'});
    const hasScope = useHasScope;
    const {id} = useParams();
    const [isExpanded, setIsExpanded] = useState(false);
    const {watch, control, setValue, register} = useFormContext();
    const [fileForImport, setAttachment] = useState<File | undefined>(undefined);
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const {
        addInvoiceAttachment,
        getSingleAttachment,
        deleteAttachment,
        getTheftMisappropriationDetail
    } = useTheftMisappropriationActions();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const editing = useRecoilValue(EDITING);
    const setTheftMisappropriate = props.setTheftMisappropriate;
    const theftMisappropriate = props.theftMisappropriate;
    const [attachmentRowId, setAttachmentRowId] = useState<number>();
    const [attachmentRowName, setAttachmentRowName] = useState<string>();
    const [openDeleteAttachmentModal, setOpenDeleteAttachmentModal] = useState(false);


    const columns: ColDef[] = [
        {
            field: "name",
            headerName: t('invoicingDetails.name'),
            unSortIcon: true,
            tooltipField: "name",
        },
        {
            field: "updateDatetime",
            headerName: t('invoicingDetails.updateDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true,
        },
        {
            field: "_actions",
            headerName: t('eventCard.action'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            width: 100,
            hide: !hasScope(THEFT_MISAPPROPRIATION_SCOPE_EDIT),
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <IconButton title="Details" color="accent"
                                    onClick={() => {
                                        getSingleAttachment(params.data.id, id).then((res) => {
                                            if (res) {
                                                const byteCharacters = atob(res.result.file);
                                                const byteNumbers = new Array(byteCharacters.length);
                                                for (let i = 0; i < byteCharacters.length; i++) {
                                                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                                                }
                                                const byteArray = new Uint8Array(byteNumbers);
                                                const blob = new Blob([byteArray], { type: res.result.contentType });

                                                const fileUrl = URL.createObjectURL(blob);

                                                let fileWindow = window.open("");
                                                if (fileWindow) {
                                                    fileWindow.document.write("<iframe width='100%' height='100%' src='" + fileUrl + "'></iframe>");
                                                }

                                                // @ts-ignore
                                                fileWindow.onunload = () => {
                                                    URL.revokeObjectURL(fileUrl);
                                                };
                                            }
                                        });
                                    }}>
                            <DescriptionRounded/>
                        </IconButton>
                        <IconButton
                            color={"accent"}
                            aria-label="delete"
                            onClick={(event) => {
                                setAttachmentRowId(params.data.id);
                                setAttachmentRowName(params.data.name);
                                setOpenDeleteAttachmentModal(true);
                            }}>
                            <DeleteRounded/>
                        </IconButton>
                    </Box>
                )
            }
        }
    ]

    // ITDRIV-22446 / LPP-24225: una scheda priva sia di rental agreement sia di cliente non e'
    // fatturabile. In tal caso lo switch "da addebitare" e il campo importo vanno disabilitati,
    // per non far entrare la scheda nel bacino dell'invio a fatturazione (che verrebbe bloccato).
    function hasChargeableSubject() {
        return !!watch("rentalAgreement") || !!watch("customerId");
    }

    function checkToBeCharged() {
        if (!hasChargeableSubject()) {
            return true;
        }
        if (!editing || watch("sentToInvoice") || (editing && !watch("toBeCharged"))) {
            return true;
        }
        if (editing && watch("toBeCharged")) {
            return false;
        }
        return true;
    }

    const toolbar = {
        renderActions: () =>
            <>
                {hasScope(THEFT_MISAPPROPRIATION_SCOPE_EDIT) &&
                    <Button size="small" component="label">
                        <Add fontSize="small"/>{t('invoicingDetails.addAttachment')}
                        <input onChange={(event) => {
                            setAttachment(event?.target?.files?.[0]);
                            const element = event.target as HTMLInputElement;
                            element.value = '';
                        }} hidden accept=".pdf, .png, .jpeg, .jpg" multiple type="file"/>
                    </Button>
                }
            </>,
        renderInfo: () =>
            <Typography align="left">{t('invoicingDetails.attachments')}</Typography>
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        pagination: false,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: true,
            resizable: false,
            wrapHeaderText: true,
            autoHeight: true,
        },
        columnDefs: columns,
        serverSideInfiniteScroll: false,
        localeText: agGridLocale,
    };

    useEffect(() => {
        if (fileForImport) {
            let fileExtension = fileForImport.name.split('.').pop();
            let allowedExtensions = ["pdf", "png", "jpeg", "jpg"]
            if (allowedExtensions.includes(fileExtension!.toLowerCase())) {
                // Upload file
                addInvoiceAttachment(fileForImport, id)
                    .then(response => {
                        if (response?.status && response.status === "OK") {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t("updateForm.successImport"),
                                        type: snackbarTypes.success,
                                        show: true,
                                        autoClose: true,
                                    }
                                }
                            });
                            getTheftMisappropriationDetail(id).then(res => {
                                setTheftMisappropriate(res);
                            });
                        } else {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t(`handleErrors.${response.message}`),
                                        type: snackbarTypes.error,
                                        show: true,
                                        autoClose: true,
                                    }
                                }
                            });
                        }
                    })
                    .finally(() => {
                        setAttachment(undefined);
                    });
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('handleErrors.wrongFileFormat'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
                setAttachment(undefined);
            }
        }
    }, [fileForImport])

    return (
        <>
            <Accordion defaultExpanded={true} onChange={(_e, expanded) => {
                setIsExpanded(expanded);
            }}>
                <AccordionSummary expandIcon={<ExpandMore/>}>
                    <Grid item md={6} xs={6}>
                        {t("tabs.invoicingDetails")}
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container columnSpacing={3} rowSpacing={1} sx={{paddingBottom: "10px"}}>
                        <Grid item xs={3}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">{t("invoicingDetails.toBeCharged")}: </Typography>
                                {(!editing || watch("sentToInvoice")) ?
                                    (watch("toBeCharged") ?
                                        <DoneAllRoundedIcon color={"success"}/> :
                                        <CloseRoundedIcon color={"error"}/>
                                    )
                                    :
                                    hasScope(THEFT_MISAPPROPRIATION_SCOPE_EDIT_TO_BE_CHARGED)?
                                        <Tooltip title={!hasChargeableSubject() ? t("invoicingDetails.notChargeableNoSubject") : ""}>
                                            <span>
                                                <Controller
                                                    control={control}
                                                    name={"toBeCharged"}
                                                    render={({field, fieldState}) => (
                                                        <FormControlLabel
                                                            labelPlacement="end"
                                                            name={"toBeCharged"}
                                                            label={""}
                                                            disabled={!editing || !hasChargeableSubject()}
                                                            sx={{columnGap: 1, userSelect: 'none', marginLeft: '2px'}}
                                                            control={
                                                                <Switch
                                                                    checked={field.value}
                                                                    onChange={(e, checked) => {
                                                                        setValue("toBeCharged", checked);
                                                                        if (!checked)
                                                                            setValue("invoiceAmount", "");
                                                                    }}
                                                                />
                                                            }
                                                        />
                                                    )}
                                                />
                                            </span>
                                        </Tooltip>
                                    :  <CloseRoundedIcon color={"error"}/>
                                }
                            </Stack>
                        </Grid>
                        <Grid item xs={3}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">{t("invoicingDetails.sentToInvoice")}: </Typography>
                                {watch("sentToInvoice") ? <DoneAllRoundedIcon color={"success"}/> :
                                    <CloseRoundedIcon color={"error"}/>}
                            </Stack>
                        </Grid>
                        <Grid item xs={3}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">{t("invoicingDetails.billed")}: </Typography>
                                {watch("billed") ? <DoneAllRoundedIcon color={"success"}/> :
                                    <CloseRoundedIcon color={"error"}/>}
                            </Stack>
                        </Grid>
                    </Grid>
                    <Grid container columnSpacing={3} rowSpacing={1}>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"invoiceAmount"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        InputLabelProps={{shrink: true}}
                                        disabled={checkToBeCharged()}
                                        label={t("invoicingDetails.invoiceAmount")}
                                        type="text"
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}/>
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"invoiceDatetime"}
                                render={({field, fieldState}) => (
                                    <DatePicker
                                        label={t("invoicingDetails.invoiceDate")}
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={true}
                                        renderInput={(params) => <TextField
                                            {...params}
                                            fullWidth
                                            InputLabelProps={{shrink: true}}
                                            disabled={true}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}/>}
                                        inputFormat={"L"}/>
                                )}/>
                        </Grid>

                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                InputLabelProps={{shrink: true}}
                                disabled={true}
                                label={t("invoicingDetails.coverageType")}
                                type="text"
                                value={watch("coverageType") ? t(`invoicingDetails.coverageTypes.${watch("coverageType")}`) : ""}/>
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                InputLabelProps={{shrink: true}}
                                disabled={true}
                                label={t("invoicingDetails.chargeableTheftExcess")}
                                type="text"
                                value={watch("chargeableTheftExcess") != null
                                    ? Number(watch("chargeableTheftExcess")).toFixed(2)
                                    : ""}/>
                        </Grid>

                        {!editing &&
                            <Grid item xs={12} sx={{padding: "16px"}}>
                                <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}
                                                   rowData={theftMisappropriate ? theftMisappropriate.attachments : []}/>
                            </Grid>}

                    </Grid>
                </AccordionDetails>
            </Accordion>
            <CustomModalBL title={t('updateForm.deleteConfirmation')}
                           content={t('updateForm.deleteConfirmationMsg', {fileName: attachmentRowName})}
                           cancelButton={t('buttons.cancel')}
                           saveButton={t('buttons.deleteDocument')}
                           open={openDeleteAttachmentModal}
                           titleIcon={<InfoRoundedIcon/>}
                           onClose={() => {
                               setOpenDeleteAttachmentModal(false);
                           }}
                           onSave={() => {
                               setOpenDeleteAttachmentModal(false);
                               deleteAttachment(attachmentRowId, id).then(res =>
                                   getTheftMisappropriationDetail(id).then(res => {
                                       setTheftMisappropriate(res);
                                   })
                               );
                           }}
            />
        </>
    );
}

export default InvoicingDetails;